import React from "react";
import GenericTable from "./GenericTable";
import { Edit, Delete, InsightsOutlined, OpenInNew } from "@mui/icons-material";
import { IconButton, Tooltip, Box, Select, MenuItem, FormControl } from "@mui/material";

const STATUS_OPTIONS = [
  { value: "draft", label: "Rascunho", color: "warning" },
  { value: "published", label: "Publicada", color: "success" },
  { value: "archived", label: "Arquivada", color: "default" },
];

const statusMeta = (status) =>
  STATUS_OPTIONS.find((option) => option.value === status) || STATUS_OPTIONS[0];

const SurveyTable = ({
  surveys = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditSurvey,
  onDeleteSurvey,
  onDeleteSelected,
  onViewAnalytics,
  onOpenPublic,
  onStatusChange,
  statusUpdatingIds = {},
  isLoading,
  canEdit = true,
  canDelete = true,
}) => {
  const rows = surveys.map((survey) => ({
    id: survey.id,
    titulo: survey.titulo || survey.title || "N/A",
    status: survey.status || "draft",
    metricType: (survey.metricType || "custom").toUpperCase(),
    startedCount: survey.summary?.startedCount ?? 0,
    completedCount: survey.summary?.completedCount ?? 0,
    completionRate:
      survey.summary?.completionRate == null ? "—" : `${survey.summary.completionRate}%`,
    data_cadastro: new Date(
      survey.data_cadastro || survey.createdAt || survey.data_ultima_alteracao
    ).toLocaleDateString(),
  }));

  const columns = [
    { field: "id", headerName: "ID", width: 72 },
    { field: "titulo", headerName: "Título", flex: 1, minWidth: 220 },
    {
      field: "metricType",
      headerName: "Métrica",
      width: 100,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            fontSize: 12,
          }}
        >
          {params.row.metricType}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const current = statusMeta(params.row.status);
        const updating = Boolean(statusUpdatingIds[params.row.id]);
        if (!canEdit || !onStatusChange) {
          return (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                fontSize: 12,
                color:
                  current.color === "success"
                    ? "success.main"
                    : current.color === "warning"
                    ? "warning.main"
                    : "text.secondary",
              }}
            >
              {current.label}
            </Box>
          );
        }
        return (
          <FormControl
            size="small"
            fullWidth
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Select
              value={params.row.status}
              disabled={updating || isLoading}
              onChange={(event) => {
                const nextStatus = event.target.value;
                if (nextStatus === params.row.status) return;
                onStatusChange(params.row.id, nextStatus);
              }}
              sx={{
                fontSize: 13,
                "& .MuiSelect-select": { py: 0.75 },
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    { field: "startedCount", headerName: "Iniciadas", width: 100 },
    { field: "completedCount", headerName: "Concluídas", width: 105 },
    { field: "completionRate", headerName: "Conclusão", width: 100 },
    { field: "data_cadastro", headerName: "Cadastro", width: 110 },
    {
      field: "actions",
      headerName: "Ações",
      width: 176,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box display="flex" gap={0.5} onClick={(event) => event.stopPropagation()}>
          <Tooltip title="Analytics">
            <IconButton size="small" onClick={() => onViewAnalytics?.(resolveSurvey(params.row))}>
              <InsightsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Link público">
            <IconButton size="small" onClick={() => onOpenPublic?.(resolveSurvey(params.row))}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              disabled={!canEdit}
              onClick={() => onEditSurvey(resolveSurvey(params.row))}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              disabled={!canDelete}
              onClick={() => onDeleteSurvey(params.row.id)}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const resolveSurvey = (row) =>
    surveys.find((item) => Number(item.id) === Number(row.id)) || row;

  const handleRowClick = (row) => {
    onViewAnalytics?.(resolveSurvey(row));
  };

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle="Pesquisas Selecionadas"
      exportFileName="pesquisas"
      onDeleteSelected={onDeleteSelected}
      onRowClick={handleRowClick}
      tableMaxHeight={520}
    />
  );
};

export default SurveyTable;
